// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { Country } from "../schema";

export function serialize_Country_internal(value: Country): unknown {
  return ((value: {
    countryCode: string;
    currency: "CHF" | "CNY" | "USD" | "EUR" | "GBP" | "INR" | "JPY";
    names: {
      ar: string;
      de: string;
      en: string;
      es: string;
      fr: string;
      pt: string;
      ru: string;
      zh: string;
    };
    phonePrefix: string;
    region: "SOUTHAMERICA" | "EUROPE" | "OCEANIA" | "NORTHAMERICA" | "AFRICA" | "ANTARCTICA" | "ASIA";
  }): unknown => {
    return {
      countryCode: ((value: string): unknown => {
        return value;
      })(value["countryCode"]),
      currency: ((value: "CHF" | "CNY" | "USD" | "EUR" | "GBP" | "INR" | "JPY"): unknown => {
        return value;
      })(value["currency"]),
      names: ((value: { ar: string; de: string; en: string; es: string; fr: string; pt: string; ru: string; zh: string }): unknown => {
        return {
          ar: ((value: string): unknown => {
            return value;
          })(value["ar"]),
          de: ((value: string): unknown => {
            return value;
          })(value["de"]),
          en: ((value: string): unknown => {
            return value;
          })(value["en"]),
          es: ((value: string): unknown => {
            return value;
          })(value["es"]),
          fr: ((value: string): unknown => {
            return value;
          })(value["fr"]),
          pt: ((value: string): unknown => {
            return value;
          })(value["pt"]),
          ru: ((value: string): unknown => {
            return value;
          })(value["ru"]),
          zh: ((value: string): unknown => {
            return value;
          })(value["zh"]),
        };
      })(value["names"]),
      phonePrefix: ((value: string): unknown => {
        return value;
      })(value["phonePrefix"]),
      region: ((value: "SOUTHAMERICA" | "EUROPE" | "OCEANIA" | "NORTHAMERICA" | "AFRICA" | "ANTARCTICA" | "ASIA"): unknown => {
        return value;
      })(value["region"]),
    };
  })(value);
}
export function serialize_Country(obj: Country): string {
  return renderJsonWithNumbersAsBigint(serialize_Country_internal(obj));
}
export function deserialize_Country_internal(value: unknown): ParserResult<Country> {
  return ((
    value: unknown,
  ): ParserResult<{
    countryCode: string;
    currency: "CHF" | "CNY" | "USD" | "EUR" | "GBP" | "INR" | "JPY";
    names: {
      ar: string;
      de: string;
      en: string;
      es: string;
      fr: string;
      pt: string;
      ru: string;
      zh: string;
    };
    phonePrefix: string;
    region: "SOUTHAMERICA" | "EUROPE" | "OCEANIA" | "NORTHAMERICA" | "AFRICA" | "ANTARCTICA" | "ASIA";
  }> => {
    if (typeof value !== "object") {
      return { success: false, reason: `[#/components/schemas/Country]: '${value}' expected to be of type 'object'.` };
    }
    if (value === null) {
      return { success: false, reason: `[#/components/schemas/Country]: '${value}' expected to not be null.` };
    }
    if (!hasProp(value, "countryCode")) {
      return { success: false, reason: `[#/components/schemas/Country/countryCode]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'countryCode'.` };
    }
    const parsed_countryCode = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/Country/countryCode]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["countryCode"]);
    })();
    if (!parsed_countryCode.success) {
      return { success: false, reason: parsed_countryCode.reason };
    }
    if (!hasProp(value, "currency")) {
      return { success: false, reason: `[#/components/schemas/Country/currency]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'currency'.` };
    }
    const parsed_currency = ((): ParserResult<"CHF" | "CNY" | "USD" | "EUR" | "GBP" | "INR" | "JPY"> => {
      return ((value: unknown): ParserResult<"CHF" | "CNY" | "USD" | "EUR" | "GBP" | "INR" | "JPY"> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/Country/currency]: '${value}' expected to be of type string.` };
        }
        switch (value) {
          case "CHF":
            return { success: true, value: "CHF" };
          case "CNY":
            return { success: true, value: "CNY" };
          case "USD":
            return { success: true, value: "USD" };
          case "EUR":
            return { success: true, value: "EUR" };
          case "GBP":
            return { success: true, value: "GBP" };
          case "INR":
            return { success: true, value: "INR" };
          case "JPY":
            return { success: true, value: "JPY" };
          default:
            return { success: false, reason: `[#/components/schemas/Country/currency]: '${value}' expected to be enum, one of 'CHF, CNY, USD, EUR, GBP, INR, JPY'.` };
        }
      })(value["currency"]);
    })();
    if (!parsed_currency.success) {
      return { success: false, reason: parsed_currency.reason };
    }
    if (!hasProp(value, "names")) {
      return { success: false, reason: `[#/components/schemas/Country/names]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'names'.` };
    }
    const parsed_names = ((): ParserResult<{
      ar: string;
      de: string;
      en: string;
      es: string;
      fr: string;
      pt: string;
      ru: string;
      zh: string;
    }> => {
      return ((
        value: unknown,
      ): ParserResult<{
        ar: string;
        de: string;
        en: string;
        es: string;
        fr: string;
        pt: string;
        ru: string;
        zh: string;
      }> => {
        if (typeof value !== "object") {
          return { success: false, reason: `[#/components/schemas/Country/names]: '${value}' expected to be of type 'object'.` };
        }
        if (value === null) {
          return { success: false, reason: `[#/components/schemas/Country/names]: '${value}' expected to not be null.` };
        }
        if (!hasProp(value, "ar")) {
          return { success: false, reason: `[#/components/schemas/Country/names/ar]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'ar'.` };
        }
        const parsed_ar = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/ar]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["ar"]);
        })();
        if (!parsed_ar.success) {
          return { success: false, reason: parsed_ar.reason };
        }
        if (!hasProp(value, "de")) {
          return { success: false, reason: `[#/components/schemas/Country/names/de]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'de'.` };
        }
        const parsed_de = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/de]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["de"]);
        })();
        if (!parsed_de.success) {
          return { success: false, reason: parsed_de.reason };
        }
        if (!hasProp(value, "en")) {
          return { success: false, reason: `[#/components/schemas/Country/names/en]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'en'.` };
        }
        const parsed_en = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/en]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["en"]);
        })();
        if (!parsed_en.success) {
          return { success: false, reason: parsed_en.reason };
        }
        if (!hasProp(value, "es")) {
          return { success: false, reason: `[#/components/schemas/Country/names/es]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'es'.` };
        }
        const parsed_es = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/es]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["es"]);
        })();
        if (!parsed_es.success) {
          return { success: false, reason: parsed_es.reason };
        }
        if (!hasProp(value, "fr")) {
          return { success: false, reason: `[#/components/schemas/Country/names/fr]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'fr'.` };
        }
        const parsed_fr = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/fr]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["fr"]);
        })();
        if (!parsed_fr.success) {
          return { success: false, reason: parsed_fr.reason };
        }
        if (!hasProp(value, "pt")) {
          return { success: false, reason: `[#/components/schemas/Country/names/pt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'pt'.` };
        }
        const parsed_pt = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/pt]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["pt"]);
        })();
        if (!parsed_pt.success) {
          return { success: false, reason: parsed_pt.reason };
        }
        if (!hasProp(value, "ru")) {
          return { success: false, reason: `[#/components/schemas/Country/names/ru]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'ru'.` };
        }
        const parsed_ru = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/ru]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["ru"]);
        })();
        if (!parsed_ru.success) {
          return { success: false, reason: parsed_ru.reason };
        }
        if (!hasProp(value, "zh")) {
          return { success: false, reason: `[#/components/schemas/Country/names/zh]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'zh'.` };
        }
        const parsed_zh = ((): ParserResult<string> => {
          return ((value: unknown): ParserResult<string> => {
            if (typeof value !== "string") {
              return { success: false, reason: `[#/components/schemas/Country/names/zh]: '${value}' expected to be of type string.` };
            }
            return { success: true, value: value };
          })(value["zh"]);
        })();
        if (!parsed_zh.success) {
          return { success: false, reason: parsed_zh.reason };
        }
        return { success: true, value: { ar: parsed_ar.value, de: parsed_de.value, en: parsed_en.value, es: parsed_es.value, fr: parsed_fr.value, pt: parsed_pt.value, ru: parsed_ru.value, zh: parsed_zh.value } };
      })(value["names"]);
    })();
    if (!parsed_names.success) {
      return { success: false, reason: parsed_names.reason };
    }
    if (!hasProp(value, "phonePrefix")) {
      return { success: false, reason: `[#/components/schemas/Country/phonePrefix]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'phonePrefix'.` };
    }
    const parsed_phonePrefix = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/Country/phonePrefix]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["phonePrefix"]);
    })();
    if (!parsed_phonePrefix.success) {
      return { success: false, reason: parsed_phonePrefix.reason };
    }
    if (!hasProp(value, "region")) {
      return { success: false, reason: `[#/components/schemas/Country/region]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'region'.` };
    }
    const parsed_region = ((): ParserResult<"SOUTHAMERICA" | "EUROPE" | "OCEANIA" | "NORTHAMERICA" | "AFRICA" | "ANTARCTICA" | "ASIA"> => {
      return ((value: unknown): ParserResult<"SOUTHAMERICA" | "EUROPE" | "OCEANIA" | "NORTHAMERICA" | "AFRICA" | "ANTARCTICA" | "ASIA"> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/Country/region]: '${value}' expected to be of type string.` };
        }
        switch (value) {
          case "SOUTHAMERICA":
            return { success: true, value: "SOUTHAMERICA" };
          case "EUROPE":
            return { success: true, value: "EUROPE" };
          case "OCEANIA":
            return { success: true, value: "OCEANIA" };
          case "NORTHAMERICA":
            return { success: true, value: "NORTHAMERICA" };
          case "AFRICA":
            return { success: true, value: "AFRICA" };
          case "ANTARCTICA":
            return { success: true, value: "ANTARCTICA" };
          case "ASIA":
            return { success: true, value: "ASIA" };
          default:
            return { success: false, reason: `[#/components/schemas/Country/region]: '${value}' expected to be enum, one of 'SOUTHAMERICA, EUROPE, OCEANIA, NORTHAMERICA, AFRICA, ANTARCTICA, ASIA'.` };
        }
      })(value["region"]);
    })();
    if (!parsed_region.success) {
      return { success: false, reason: parsed_region.reason };
    }
    return { success: true, value: { countryCode: parsed_countryCode.value, currency: parsed_currency.value, names: parsed_names.value, phonePrefix: parsed_phonePrefix.value, region: parsed_region.value } };
  })(value);
}
export function deserialize_Country(str: string): ParserResult<Country> {
  return deserialize_Country_internal(parseJsonWithNumbersAsBigint(str));
}
